import {
  HeartIcon,
  PhoneIcon,
  VerifiedIcon,
  WhatsappIcon,
} from "@/components/Icons";
import { PropertyShareAction } from "@/components/Property/QuickAction";
import { Button } from "@/components/ui/Button";
import { P } from "@/components/ui/Heading";
import { PropertyInfoIcons } from "@/components/ui/PropertyInfoIcons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { Locale } from "@/enums/common.enum";
import { BoostType, ListingType } from "@/enums/property.enum";
import { useAddToFavorites, useRemoveFavorite } from "@/hooks/useFavorite";
import { Property } from "@/interfaces/property.interface";
import { gTagEvents } from "@/lib/gtag";
import { makeWhatsappLink } from "@/lib/property/helpers";
import { formatNumber } from "@/lib/utils";
import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { nanoid } from "nanoid";
import { useLocale, useTranslations } from "next-intl";
import { MouseEvent } from "react";
export interface ContentSectionProps {
  listingId: string;
  property: Property;
  containerClassName?: string;
  cardType?: "list" | "showcase";
}

export const ContentSection = ({
  listingId,
  property,
  containerClassName,
  cardType = "list",
}: ContentSectionProps) => {
  const isRental = property.listingType === ListingType.RENT;

  const locale = useLocale() as Locale;
  const t = useTranslations("shared");
  const ft = useTranslations("favorite");
  const { isSignedIn } = useAuth();

  const addToFavorites = useAddToFavorites();
  const removeFromFavorite = useRemoveFavorite();

  const isPending = addToFavorites.isPending || removeFromFavorite.isPending;

  const handleWhatsappClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    const inquiryId = nanoid();
    const link = makeWhatsappLink(property, inquiryId, locale);

    gTagEvents.whatsappClick(property, listingId!, inquiryId);
    window.open(link, "_blank");
  };

  const handleCallClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    const inquiryId = nanoid();
    gTagEvents.callClick(property, listingId!, inquiryId);

    window.open(`tel:${property.agent.phone}`);
  };

  const handleFavoriteClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    if (!property.isFavorite && isSignedIn) {
      addToFavorites.mutateAsync(
        { id: property.id },
        {
          onSuccess: () => {
            property.isFavorite = true;
          },
        }
      );
    } else {
      removeFromFavorite.mutateAsync(
        { id: property.id },
        {
          onSuccess: () => {
            property.isFavorite = false;
          },
        }
      );
    }
  };

  return (
    <div
      className={clsx(
        "!flex flex-col justify-center gap-3 bg-white items-stretch",
        containerClassName
      )}
    >
      <div className="flex items-end justify-between">
        <div className="flex items-end gap-1">
          <P className="text-lg font-bold text-gray-900 line-clamp-1">
            {property.price
              ? t("words.currency", { value: formatNumber(property.price) })
              : t("words.ask-for-price")}
            {isRental && property.price ? (
              " / " +
              t(
                `filters.rental-period.options.${property.rentalPeriod}`
              )?.toLowerCase()
            ) : (
              <></>
            )}
          </P>
          {property.isFairPrice && (
            <VerifiedIcon className="h-6 w-6 fill-yalla-value" />
          )}
        </div>
        <PropertyShareAction property={property} />
      </div>

      <div className="flex flex-wrap gap-3 justify-between w-full sm:items-end flex-col sm:flex-row h-full">
        <PropertyInfoIcons property={property} size="md" />

        <div
          className={clsx(
            "flex gap-2 items-end justify-end self-end justify-self-end",
            {
              "w-full":
                property.boostType !== BoostType.PREMIUM &&
                cardType !== "showcase",
            }
          )}
        >
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Button
                className="w-fit p-2"
                variant={"outline"}
                onClick={handleFavoriteClick}
                disabled={!isSignedIn}
              >
                <HeartIcon
                  className={clsx("size-4", {
                    "fill-gray-600": !property.isFavorite,
                    "fill-primary": property.isFavorite,
                    "animate-pulse": isPending,
                  })}
                />
              </Button>
            </TooltipTrigger>
            {!isSignedIn && (
              <TooltipContent>{ft("action.click")}</TooltipContent>
            )}
          </Tooltip>
          <Button
            variant={"outline"}
            className="w-fit p-2"
            onClick={handleCallClick}
          >
            <PhoneIcon className="size-4" />
          </Button>

          <Button className="w-fit p-2" onClick={handleWhatsappClick}>
            <WhatsappIcon className="size-5 fill-white" />
          </Button>
        </div>
      </div>
    </div>
  );
};
